import { memo, useCallback, useMemo } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import { CheckIcon, ImageIcon, ZapIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import {
  DropdownMenu,
  DropdownMenuLabel,
  type DropdownMenuOption,
  renderDropdownItems,
} from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';

import { ItemPicker, type ItemPickerSelection } from '~/components/ItemPicker/ItemPicker';
import { UpsellPopover } from '~/components/ShareModal/components/UpsellPopover';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';

/**
 * Logo/cover accept images only: photo, or animated for GIFs. Matches the prototype's image
 * filter and the backend's write-time validation of the design image reference. Exported so the
 * collection link editor's on-canvas "Change cover" opens the SAME workspace picker filter this
 * row's "Choose from workspace…" opens.
 */
export const DESIGN_IMAGE_ASSET_TYPES: ClipType[] = [ClipType.photo, ClipType.animated];

/** A pre-resolved image choice (e.g. a brand-kit image) rendered as a dropdown option. */
export type ShareDesignImageOption = {
  id: string;
  label: string;
  /**
   * The brand field the image belongs to (e.g. "Logos"), rendered as an uppercase group label above
   * the field's entries. Options without one render ungrouped (no label).
   */
  fieldName?: string;
  imageSrc: string;
  onSelect: () => void;
};

/** `options` bucketed by source brand field; `fieldName` is absent for the ungrouped bucket. */
type BrandImageOptionGroup = {
  fieldName?: string;
  options: ShareDesignImageOption[];
};

export type ShareDesignImageButtonProps = {
  /** data-testid for the picker wrapper. */
  'data-testid'?: string;
  disabled: boolean;
  /**
   * Trigger copy (e.g. "Choose logo"). Always shown — the trigger label doesn't change when an
   * image is set (matching the prototype's plain-button treatment); the preview surface renders
   * the selected image instead.
   */
  emptyLabel: string;
  /**
   * Whether a value is currently stored, independent of whether a preview URL has resolved —
   * gates the dropdown's "Remove image" item. A stored brand-kit ref can lack a resolvable
   * `imageSrc` until its save round-trips, but it must still be removable. Defaults to
   * `!!imageSrc` for callers whose values always resolve a URL (e.g. the board cover).
   */
  hasValue?: boolean;
  /**
   * Resolved image URL of the current value (just-picked transient ?? server-resolved). Not
   * rendered in the trigger — the preview surface renders it; `hasValue` (defaulting to its
   * presence) gates the dropdown's "Remove image" item.
   */
  imageSrc?: string;
  /**
   * Extra image options (e.g. brand-kit images) rendered as dropdown items above
   * "Choose from workspace…". When non-empty, the empty-state click opens the dropdown
   * instead of going straight to the workspace item picker.
   */
  options?: ShareDesignImageOption[];
  /**
   * The `options` entry backing the current image, if any. Purely display: the dropdown marks that
   * entry selected.
   */
  selectedOptionId?: string;
  /** Copy for the item picker's confirm button. */
  selectModalConfirmLabel?: string;
  /** Heading for the item picker. */
  selectModalTitle?: string;
  /** data-testid for the picker trigger button; dropdown item ids derive from it. */
  selectTestId: string;
  /**
   * Plan-gate-only upsell affordance. Kept separate from `disabled` (which also covers in-flight
   * saves) so the flash icon reflects ONLY the plan gate and doesn't flash during saves.
   *
   * When set, it REPLACES the picker: the button carries the flash and opens the upsell instead of
   * the image options, and `disabled` no longer applies (see the gated branch below).
   */
  showUpsell: boolean;
  /** Called with the picked clip so the parent can derive its reference + preview URL. */
  onSelect: (clip: Clip) => void;
  /** Called when the user clears the current image. */
  onClear: () => void;
};

/**
 * Design-tab image picker (custom logo, cover image). One plain gray button trigger (matching the
 * prototype's `AssetPickerRow`): empty with no extra options it opens the workspace item picker
 * directly; otherwise it opens a dropdown of image options + "Choose from workspace…" + "Remove
 * image" (when an image is set), for the side-by-side layout in the "Site design" section.
 */
export const ShareDesignImageButton = memo(
  ({
    'data-testid': dataTestId,
    disabled,
    emptyLabel,
    hasValue,
    imageSrc,
    options = [],
    selectedOptionId,
    selectModalConfirmLabel,
    selectModalTitle,
    selectTestId,
    showUpsell,
    onSelect,
    onClear,
  }: ShareDesignImageButtonProps) => {
    const [showItemPicker] = useAirModal(ItemPicker);

    // Single-select, so at most one clip; guard the empty case anyway (confirm is disabled at 0).
    const handleItemPickerConfirm = useCallback(
      ({ clips }: ItemPickerSelection) => {
        const [clip] = clips;

        if (clip) {
          onSelect(clip);
        }
      },
      [onSelect],
    );

    const handleOpenWorkspacePicker = useCallback(() => {
      showItemPicker({
        assetTypes: DESIGN_IMAGE_ASSET_TYPES,
        confirmLabel: selectModalConfirmLabel,
        itemTypes: ['asset'],
        onConfirm: handleItemPickerConfirm,
        title: selectModalTitle,
      });
    }, [showItemPicker, selectModalConfirmLabel, selectModalTitle, handleItemPickerConfirm]);

    // A stored value can exist without a resolved preview URL (brand-kit ref pre-save), so removal
    // gating rides on `hasValue`, falling back to URL presence for callers that don't pass it.
    const hasStoredValue = hasValue ?? !!imageSrc;

    // No value and nothing to list — skip the pointless empty dropdown and go straight to the modal.
    const hasDropdownOptions = hasStoredValue || options.length > 0;

    const dropdownOptions = useMemo(() => {
      // One labelled section per source brand field (e.g. "LOGOS" over the logo entries), mirroring
      // the font row's grouping; options without a fieldName render in an unlabelled group
      // (defensive — mapBrandImages always sets one). Insertion order is preserved.
      const groups: BrandImageOptionGroup[] = [];

      for (const option of options) {
        const group = groups.find((candidate) => candidate.fieldName === option.fieldName);

        if (group) {
          group.options.push(option);
        } else {
          groups.push({ fieldName: option.fieldName, options: [option] });
        }
      }

      const items: DropdownMenuOption[] = [];

      for (const group of groups) {
        if (group.fieldName) {
          // `type: 'children'` instead of `type: 'title'` only so the label can carry the
          // namespaced testid — `renderDropdownItems` has no data-testid passthrough for titles.
          // Same `DropdownMenuLabel` primitive (uppercase group-label styling) either way.
          items.push({
            children: (
              // biome-ignore lint/plugin: PLA-1724 — pre-existing debt, not introduced by the icon migration
              <DropdownMenuLabel data-testid={`${selectTestId}_BRAND_GROUP_LABEL`}>{group.fieldName}</DropdownMenuLabel>
            ),
            id: `${selectTestId}_BRAND_GROUP_${group.fieldName}`,
            type: 'children',
          });
        }

        for (const option of group.options) {
          items.push({
            id: option.id,
            label: option.label,
            onSelect: option.onSelect,
            prefix: (
              <span
                aria-hidden="true"
                className="size-5 shrink-0 rounded-4 border ads:border-default border-gray-4 ads:bg-subtle bg-center bg-contain bg-gray-2 bg-no-repeat"
                style={{ backgroundImage: `url(${option.imageSrc})` }}
              />
            ),
            // Same selected idiom as the primitive's SelectDropdownMenu.
            suffix:
              option.id === selectedOptionId ? (
                <CheckIcon className="ads:text-icon-primary text-blue-9" size="sm" />
              ) : undefined,
            type: 'item',
          });
        }
      }

      if (items.length > 0) {
        items.push({ id: `${selectTestId}_OPTIONS_SEPARATOR`, type: 'separator' });
      }

      items.push({
        id: `${selectTestId}_WORKSPACE_OPTION`,
        label: 'Choose from workspace…',
        onSelect: handleOpenWorkspacePicker,
        prefix: <ImageIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
        type: 'item',
      });

      if (hasStoredValue) {
        items.push({ id: `${selectTestId}_REMOVE_SEPARATOR`, type: 'separator' });
        items.push({
          className: 'ads:text-error text-red-9',
          id: `${selectTestId}_REMOVE_OPTION`,
          label: 'Remove image',
          onSelect: onClear,
          type: 'item',
        });
      }

      return items;
    }, [options, selectedOptionId, hasStoredValue, selectTestId, handleOpenWorkspacePicker, onClear]);

    const trigger = (
      <Button
        appearance="filled"
        className="w-full"
        color="gray"
        data-testid={selectTestId}
        disabled={disabled}
        onClick={hasDropdownOptions ? undefined : handleOpenWorkspacePicker}
        size="medium"
        type="button"
      >
        {emptyLabel}
      </Button>
    );

    /*
     * Plan-gated: the picker button IS the upsell affordance (FOUND-700). The flash rides in the
     * Button's `suffix` slot rather than beside the button, because a sibling flash is a block of
     * its own inside this row's box and pushed the trigger out of line with the sibling picker
     * ("Choose logo" sitting lower than "Choose cover").
     *
     * The whole button opens the upsell, following EnrichmentUpsellButton — which also means it is
     * NOT rendered `disabled`: a disabled button dispatches no click, so the upsell would be
     * unreachable. There is nothing to protect by disabling it either, since this branch replaces
     * the picker outright: no dropdown, no item picker, no write.
     */
    if (showUpsell) {
      return (
        <div className="flex-1" data-testid={dataTestId}>
          <UpsellPopover
            trigger={
              <Button
                appearance="filled"
                className="w-full"
                color="gray"
                data-testid={selectTestId}
                size="medium"
                suffix={
                  // Carries the affordance's testid, so "this row is gated" reads the same here as
                  // it does for rows whose flash is a button beside the control.
                  <span className="flex items-center" data-testid="UPSELL_FLASH_ICON_BUTTON">
                    <ZapIcon aria-hidden className="ads:text-icon-primary text-blue-9" size="sm" />
                  </span>
                }
                type="button"
              >
                {emptyLabel}
              </Button>
            }
          >
            {SHARE_MODAL_UPSELL_MESSAGE}
          </UpsellPopover>
        </div>
      );
    }

    return (
      <div className="flex-1" data-testid={dataTestId}>
        {hasDropdownOptions ? (
          <DropdownMenu disabled={disabled} trigger={trigger}>
            {renderDropdownItems({ options: dropdownOptions })}
          </DropdownMenu>
        ) : (
          trigger
        )}
      </div>
    );
  },
);

ShareDesignImageButton.displayName = 'ShareDesignImageButton';
